import { useEffect, useState } from 'react';
import { generatePath, useLocation, useParams } from 'react-router-dom';

import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { useDeleteModal } from '@components/generic-modals/delete-modal';
import { RouterTabsHeader } from '@components/header/router-tabs-header';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconDrag } from '@components/icons/drag';
import { IconEdit } from '@components/icons/edit';
import { IconInfoSimple } from '@components/icons/info-simple';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconPortal } from '@components/icons/portal';
import { IconRedo } from '@components/icons/redo';
import { IconRemove } from '@components/icons/remove';
import { IconSecurity } from '@components/icons/security';
import { IconSettings } from '@components/icons/settings';
import { IconTextFormatList } from '@components/icons/text-format-list';
import { IconTrash } from '@components/icons/trash';
import { IconUrl } from '@components/icons/url';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PortalCard } from '@components/portal-card';
import { forceHideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import {
  useDeletePortalLink,
  usePortal,
  useRemoveJourneyFormFromPortal,
  useUpdatePortal,
  useUpdatePortalItemOrder,
} from 'api/portals/hooks';
import { PortalDetail, PortalForm, PortalLink } from 'api/portals/requests';
import { useEditJourneyFormOverlay } from 'ui/actions/triggers/journey-form/edit-journey-form-overlay';
import { routePaths } from 'ui/authenticated/navigation';

import { PortalSecurity, PortalSettings } from './portal-settings';
import { useAddCustomLinkSidebar } from './sidebars/add-custom-link';
import { useAddToPortalSidebar } from './sidebars/add-to-portal-sidebar';
import { usePortalDetailsSidebar } from './sidebars/portal-edit-sidebar';

export const PortalDetails = () => {
  const { portalId = '' } = useParams();
  const { t } = useScopedTranslate(locale);
  const location = useLocation();

  const pathSegments = location.pathname.split('/');
  const section = pathSegments[pathSegments.length - 1] || 'layout';

  const { data, isLoading } = usePortal(portalId);
  const showAddToPortalSidebar = useAddToPortalSidebar();
  const updatePortalMutation = useUpdatePortal(portalId);
  const showPortalDetailsSidebar = usePortalDetailsSidebar();

  if (isLoading || !data) {
    return <Spinner />;
  }

  const tabs = [
    {
      label: t('layout'),
      section: 'layout',
      icon: <IconTextFormatList />,
      content: <PortalLayout portal={data} />,
    },
    {
      label: t('settings'),
      section: 'settings',
      icon: <IconSettings />,
      content: <PortalSettings portal={data} />,
    },
    {
      label: t('security'),
      section: 'security',
      icon: <IconSecurity />,
      content: <PortalSecurity portal={data} />,
    },
  ];

  const handleActiveChange = async (value: boolean) => {
    await updatePortalMutation.mutateAsync({ isActive: value });
  };

  return (
    <RouterTabsHeader
      breadcrumbs={
        <Breadcrumb
          items={[
            { label: t('portals'), link: generatePath(routePaths.portals) },
            { label: data.name },
          ]}
        />
      }
      tabs={tabs}
      actions={
        <Stack space={4}>
          <Stack orientation="horizontal" space={2}>
            <Button
              variant="secondary"
              text={t('openPortal')}
              iconRight={<IconOpenNewTab color="buttonIconPositive" size={16} />}
              href={data.url ?? `${process.env.VITE_PORTAL_URL}/${data.slugPath}`}
              target="_blank"
            />
            {section === 'layout' && (
              <Button
                variant="primary"
                text={t('addToPortal')}
                iconLeft={<IconAddPlus />}
                onClick={() => showAddToPortalSidebar({ portalShortId: data.shortId })}
              />
            )}
            <Button
              variant="secondary"
              text={t('details')}
              iconLeft={<IconInfoSimple color="purple50" />}
              onClick={() => showPortalDetailsSidebar({ portal: data })}
            />
          </Stack>
          {section === 'layout' && (
            <Stack orientation="horizontal" space={2} justify="flex-end">
              <Text font="regular14" text={t('active')} />
              <Switch value={data.isActive} onChange={handleActiveChange} />
            </Stack>
          )}
        </Stack>
      }
    />
  );
};

type PortalItem = (PortalForm & { type: 'form' }) | (PortalLink & { type: 'link' });

type DraggingState = {
  id: string;
  order: number;
  index: number;
} | null;

const PortalLayout = ({ portal }: { portal: PortalDetail }) => {
  const { t } = useScopedTranslate(locale);

  const allItems: PortalItem[] = [
    ...portal.forms.map((form) => ({ ...form, type: 'form' as const })),
    ...portal.links.map((link) => ({ ...link, type: 'link' as const })),
  ].sort((a, b) => a.order - b.order);

  const [items, setItems] = useState<PortalItem[]>(allItems);
  const [draggingItem, setDraggingItem] = useState<DraggingState>(null);

  useEffect(() => {
    setItems(allItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portal.forms, portal.links]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    })
  );

  const itemIds = items.map((item) => item.shortId);
  const draggingPortalItem = draggingItem ? items[draggingItem.index] : null;

  const updateOrderMutation = useUpdatePortalItemOrder(portal.shortId);

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const order = active.data.current?.order;
    setDraggingItem({
      order: order,
      index: active.data.current?.sortable.index,
      id: String(active.id),
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    if (!draggingItem) return;
    const { active, over } = event;
    setDraggingItem(null);
    if (!over || active.id === over.id) return;

    const overIndex = itemIds.indexOf(String(over.id));
    const activeIndex = itemIds.indexOf(String(active.id));
    changeItemOrder(activeIndex, overIndex);
  };

  const onDragCancel = () => {
    setDraggingItem(null);
  };

  const changeItemOrder = (activeIndex: number, overIndex: number) => {
    const newItems = [...items];
    const [removed] = newItems.splice(activeIndex, 1);
    newItems.splice(overIndex, 0, removed);

    const updatedItems = newItems.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    setItems(updatedItems);

    const orderData = updatedItems.map((item) => ({
      shortId: item.shortId,
      order: item.order,
      type: item.type,
    }));

    updateOrderMutation.mutate({
      items: orderData,
    });
  };

  const showEditLinkSidebar = useAddCustomLinkSidebar();
  const showEditJourneyForm = useEditJourneyFormOverlay();
  const handleEdit = (item: PortalItem) => {
    if (item.type === 'link') {
      showEditLinkSidebar({
        portalShortId: portal.shortId,
        linkShortId: item.shortId,
      });
    } else if (item.type === 'form') {
      showEditJourneyForm({
        journeyId: item.journeyShortId,
        formId: item.formShortId,
      });
    }
  };

  if (items.length === 0) {
    return (
      <EmptyState
        icon={<IconPortal size={80} />}
        content={t('emptyState')}
        header={t('nothingAddedYet')}
      />
    );
  }

  return (
    <ScrollContainer>
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <DragOverlay>
          {draggingPortalItem && (
            <PortalCard
              name={draggingPortalItem.name}
              description={draggingPortalItem.description ?? ''}
              image={draggingPortalItem.imageUrl}
              actions={
                <Stack
                  orientation="horizontal"
                  space={4}
                  align="center"
                  style={{ padding: pxToRem(8) }}
                >
                  <IconEdit color="buttonIconPositive" />
                  <CardActions
                    item={draggingPortalItem}
                    handleEdit={() => handleEdit(draggingPortalItem)}
                    portalShortId={portal.shortId}
                  />
                </Stack>
              }
            />
          )}
        </DragOverlay>

        <SortableContext items={itemIds} strategy={rectSortingStrategy}>
          <ItemsGrid>
            {items.map((item) => (
              <DraggablePortalCard
                key={item.shortId}
                item={item}
                isDragging={draggingPortalItem?.shortId === item.shortId}
                portalShortId={portal.shortId}
                handleEdit={() => handleEdit(item)}
              />
            ))}
          </ItemsGrid>
        </SortableContext>
      </DndContext>
    </ScrollContainer>
  );
};

type DraggablePortalCardProps = {
  item: PortalItem;
  isDragging: boolean;
  portalShortId: string;
  handleEdit: (item: PortalItem) => void;
};

const DraggablePortalCard = ({
  item,
  isDragging,
  handleEdit,
  portalShortId,
}: DraggablePortalCardProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: item.shortId,
  });
  const isLink = item.type === 'link';

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const cardProps = {
    name: item.name,
    description: item.description ?? '',
    image: item.imageUrl,
    imageDragProps: listeners,
    topIcons: (
      <Stack orientation="horizontal" space={2} align="center">
        <IconDrag color="buttonIconPositive" />
        {isLink && <IconUrl color="buttonIconPositive" />}
      </Stack>
    ),
    ...((item.type === 'link' || item.hasAccessToWorkspace) && {
      actions: (
        <Stack orientation="horizontal" space={4} align="center" style={{ padding: pxToRem(8) }}>
          <IconButton
            icon={<IconEdit color="buttonIconPositive" />}
            onClick={() => handleEdit(item)}
            variant="ghost-white"
            dataTestid="portal-card-edit-button"
          />
          <CardActions item={item} handleEdit={handleEdit} portalShortId={portalShortId} />
        </Stack>
      ),
    }),
  };

  return (
    <DraggableWrapper ref={setNodeRef} style={style} {...attributes}>
      <PortalCard {...cardProps} />
    </DraggableWrapper>
  );
};

const CardActions = ({
  item,
  handleEdit,
  portalShortId,
}: {
  item: PortalItem;
  handleEdit: (item: PortalItem) => void;
  portalShortId: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const showDelete = useDeleteModal();
  const deleteLinkMutation = useDeletePortalLink(portalShortId);
  const removeFormMutation = useRemoveJourneyFormFromPortal();

  const isLink = item.type === 'link';

  return (
    <Menu
      buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'ghost' }}
      placement="left-start"
      onSelect={() => handleEdit(item)}
      dataTestid="portal-card-actions"
    >
      <Menu.Item
        label={isLink ? t('editLink') : t('editForm')}
        icon={<IconEdit color="elementPurple80" />}
        onSelect={() => handleEdit(item)}
      />
      {!isLink && (
        <Menu.ItemLink
          to={generatePath(routePaths.editJourney, {
            workspaceId: item.workspaceShortId,
            journeyId: item.journeyShortId,
            '*': '',
          })}
          icon={<IconRedo color="elementPurple80" />}
          label={t('openJourneyForm')}
        />
      )}
      <Menu.Item
        label={isLink ? t('deleteFromPortal') : t('removeFromPortal')}
        icon={isLink ? <IconTrash color="elementRed100" /> : <IconRemove color="elementRed100" />}
        variant="danger"
        onSelect={() =>
          showDelete({
            title: isLink ? t('deleteLinkTitle') : t('removeFormTitle'),
            description: isLink ? t('deleteLinkDescription') : t('removeFormDescription'),
            buttonText: t('core.delete'),
            hasUserConfirmation: true,
            onConfirm: () =>
              isLink
                ? deleteLinkMutation.mutateAsync(item.shortId, { onSuccess: forceHideSidebar })
                : removeFormMutation.mutateAsync(
                    {
                      portalShortId,
                      formShortId: item.shortId,
                    },
                    { onSuccess: forceHideSidebar }
                  ),
          })
        }
      />
    </Menu>
  );
};

const ItemsGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 320px)',
  gap: pxToRem(24),
  padding: pxToRem(24),
  justifyContent: 'center',

  '@media (min-width: 1640px)': {
    gridTemplateColumns: 'repeat(4, 320px)',
  },

  '@media (min-width: 1980px)': {
    gridTemplateColumns: 'repeat(5, 320px)',
  },
});

const DraggableWrapper = styled.div({
  position: 'relative',
  cursor: 'default',
  transition: 'all 0.2s ease',
});

const ScrollContainer = styled.div({
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
});

const locale = {
  en: {
    portals: 'Portals',
    layout: 'Layout',
    settings: 'Settings',
    security: 'Security',
    openPortal: 'Open Portal',
    addToPortal: 'Add to Portal',
    active: 'Active',
    editForm: 'Edit form',
    editLink: 'Edit link',
    openJourneyForm: 'Open journey',
    nothingAddedYet: 'Nothing added yet',
    emptyState:
      'External forms created in the Journey Builder, along with custom links, can be added here for display on your company portal',
    removeFromPortal: 'Remove from portal',
    deleteFromPortal: 'Delete from portal',
    deleteLinkTitle: 'Delete link',
    deleteLinkDescription: 'Are you sure you want to delete this link?',
    removeFormTitle: 'Remove form',
    removeFormDescription: 'Are you sure you want to remove this form?',
    details: 'Details',
  },
};
